/**
 * /dashboard/timesheet/admin/projects/$id — Project detail +
 * assignments management (G3 T.3c; SOP §1.2; Phase 3b — 2026-06-01).
 *
 * RBAC: GET /api/v1/projects/{id} is reachable by any authed tenant
 * user (server-scoped — non-admin sees only their assigned active
 * projects, gets 404 otherwise). GET / POST /assignments are HR-admin.
 * The page hard role-gates the surface to HR-admin only — a non-admin
 * is bounced to /dashboard. The backend is the real boundary and
 * re-checks every request.
 *
 * Layout:
 *  1. Header: title + "Back to projects" button + (HR-admin) "Edit".
 *  2. Detail card: code / name / client / description / billable /
 *     rate / requires-assignment / status / timestamps.
 *  3. Assignments card: the current assigned-employee list (joined
 *     name + email) + "Manage assignments" sheet.
 *
 * The assignments section shows an informational message when
 * `requires_assignment === false` — selections are advisory until the
 * admin toggles it on via Edit (mirrors the T.2a contract: open access
 * means anyone can log time regardless of selections).
 *
 * Migration note: port of
 * frontend/src/app/(dashboard)/dashboard/timesheet/admin/projects/[id]/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - use(params) (Next.js) → Route.useParams() (TanStack)
 *   - useRouter (next/navigation) → useNavigate (@tanstack/react-router)
 *   - next/link → @tanstack/react-router Link (typed)
 */

import { useMemo } from "react";
import { createFileRoute, Link, useNavigate , useParams} from "@tanstack/react-router";
import axios from "axios";
import {
  ArrowLeft,
  Info,
  RefreshCw,
  UsersRound,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import type { Role } from "@/types/auth";
import { toApiError } from "@/lib/api";
import {
  useProject,
  useProjectAssignments,
} from "@/hooks/use-timesheet";
import { formatPaise } from "@/lib/expense-format";
import type { ProjectAssignmentRow } from "@/types/timesheet";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { StatusBadge } from "@/components/shared/status-badge";
import { TimesheetFeatureGate } from "@/components/timesheet/timesheet-feature-gate";
import { ProjectFormDialog } from "@/components/timesheet/project-form-dialog";
import { ProjectDeleteButton } from "@/components/timesheet/project-delete-button";
import { ProjectAssignmentsSheet } from "@/components/timesheet/project-assignments-sheet";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/timesheet/admin/projects/$id",
)({
  component: ProjectDetailRoute,
});

const HR_ADMINS = new Set<Role>(["company_admin", "hr_manager"]);

const ASSIGN_SKELETON_KEYS = [
  "pas-sk-0",
  "pas-sk-1",
  "pas-sk-2",
] as const;

function statusOf(err: unknown): number | undefined {
  return axios.isAxiosError(err) ? err.response?.status : undefined;
}

function isNotFound(err: unknown): boolean {
  return statusOf(err) === 404;
}

/** dd Mon yyyy HH:mm — localised to en-IN per the standing convention. */
function fmtDateTime(iso: string | undefined | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Row({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border border-input px-3 py-2">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-right text-sm font-medium text-gray-900">
        {value}
      </dd>
    </div>
  );
}

function ProjectDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const canAdmin = !!user?.role && HR_ADMINS.has(user.role);

  const projQ = useProject(numId, Number.isFinite(numId) && numId > 0);
  const assignQ = useProjectAssignments(
    numId,
    canAdmin && Number.isFinite(numId) && numId > 0,
  );

  const assignments = useMemo<ProjectAssignmentRow[]>(() => {
    const items = assignQ.data?.items ?? [];
    return [...items].sort((a, b) =>
      a.employee_full_name.localeCompare(b.employee_full_name),
    );
  }, [assignQ.data]);

  // The admin gate is the same as the list page. A non-admin who reaches
  // a deep link is bounced — they have no business on this surface. Run
  // AFTER every hook above to keep the call-order stable across renders.
  if (!loading && user && !canAdmin) {
    void navigate({ to: "/app/$tenantSlug", params: { tenantSlug }, replace: true });
    return null;
  }

  const project = projQ.data;
  const projMissing = isNotFound(projQ.error);
  const projHardError = projQ.error && !projMissing;

  return (
    <TimeAndLeaveShell
      title={project ? project.name : "Project"}
      description={project ? `Code · ${project.code}` : undefined}
    >
      <TimesheetFeatureGate>
        <div className="mb-4 flex flex-wrap justify-end gap-2">
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug/timesheet/admin/projects" params={{ tenantSlug }}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
          {project && canAdmin && (
            <>
              <ProjectFormDialog existing={project} />
              {project.is_active && (
                <ProjectDeleteButton project={project} />
              )}
            </>
          )}
        </div>

        {projQ.isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        )}

        {projMissing && (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
              <p className="text-base font-medium text-gray-900">
                Project not found
              </p>
              <p className="max-w-md text-sm text-muted-foreground">
                The project may have been removed, or the backend
                endpoint isn&apos;t deployed yet. Return to the list and
                try again.
              </p>
              <Button asChild variant="outline" className="gap-1.5">
                <Link to="/app/$tenantSlug/timesheet/admin/projects" params={{ tenantSlug }}>
                  <ArrowLeft className="h-4 w-4" />
                  Back to Projects
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {projHardError && (
          <Alert variant="destructive">
            <AlertTitle>Could not load this project</AlertTitle>
            <AlertDescription className="flex flex-col gap-2">
              <span>{toApiError(projQ.error).message}</span>
              <span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => projQ.refetch()}
                  className="gap-1.5"
                >
                  <RefreshCw className="h-4 w-4" />
                  Retry
                </Button>
              </span>
            </AlertDescription>
          </Alert>
        )}

        {project && (
          <>
            <Card className="mb-5">
              <CardHeader>
                <CardTitle className="text-base">Details</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid gap-2 sm:grid-cols-2">
                  <Row label="Code" value={project.code} />
                  <Row
                    label="Status"
                    value={
                      <StatusBadge
                        tone={project.is_active ? "success" : "neutral"}
                        label={project.is_active ? "Active" : "Inactive"}
                      />
                    }
                  />
                  <Row
                    label="Client"
                    value={
                      project.client_name?.trim()
                        ? project.client_name
                        : "—"
                    }
                  />
                  <Row
                    label="Billable"
                    value={
                      project.is_billable ? (
                        <Badge variant="secondary">Billable</Badge>
                      ) : (
                        <span className="text-muted-foreground">No</span>
                      )
                    }
                  />
                  <Row
                    label="Default Rate"
                    value={
                      project.billing_rate_paise != null
                        ? `${formatPaise(project.billing_rate_paise)}/hr`
                        : "—"
                    }
                  />
                  <Row
                    label="Assignment"
                    value={
                      project.requires_assignment ? (
                        <Badge variant="outline">Restricted</Badge>
                      ) : (
                        <span className="text-muted-foreground">
                          Open access
                        </span>
                      )
                    }
                  />
                  <Row
                    label="Created"
                    value={fmtDateTime(project.created_at)}
                  />
                  <Row
                    label="Updated"
                    value={fmtDateTime(project.updated_at)}
                  />
                </dl>
                {project.description?.trim() && (
                  <div className="mt-4 rounded-md border border-input px-3 py-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      Description
                    </p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-gray-900">
                      {project.description}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-base">Assignments</CardTitle>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {project.requires_assignment
                        ? "Only assigned employees can log time against this project."
                        : "Open access — any employee can log time. Selections below are advisory."}
                    </p>
                  </div>
                  {canAdmin && (
                    <ProjectAssignmentsSheet project={project} />
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {!project.requires_assignment && (
                  <Alert className="mb-4">
                    <Info className="h-4 w-4" />
                    <AlertTitle>Open access</AlertTitle>
                    <AlertDescription>
                      Toggle{" "}
                      <span className="font-medium">
                        Requires assignment
                      </span>{" "}
                      on the Edit dialog to enforce these selections.
                    </AlertDescription>
                  </Alert>
                )}

                {assignQ.isLoading && (
                  <div className="space-y-2">
                    {ASSIGN_SKELETON_KEYS.map((k) => (
                      <Skeleton key={k} className="h-12 w-full" />
                    ))}
                  </div>
                )}

                {assignQ.isError && !assignQ.isLoading && (
                  <Alert variant="destructive">
                    <AlertTitle>Could not load assignments</AlertTitle>
                    <AlertDescription className="flex flex-col gap-2">
                      <span>{toApiError(assignQ.error).message}</span>
                      <span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => assignQ.refetch()}
                          className="gap-1.5"
                        >
                          <RefreshCw className="h-4 w-4" />
                          Retry
                        </Button>
                      </span>
                    </AlertDescription>
                  </Alert>
                )}

                {!assignQ.isLoading &&
                  !assignQ.isError &&
                  assignments.length === 0 && (
                    <div className="flex flex-col items-center gap-3 rounded-md border border-dashed border-input py-10 text-center">
                      <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
                        <UsersRound className="h-5 w-5" />
                      </span>
                      <p className="text-sm text-muted-foreground">
                        No assignments yet.
                        {canAdmin
                          ? " Use Manage assignments to pick employees."
                          : ""}
                      </p>
                    </div>
                  )}

                {!assignQ.isLoading &&
                  !assignQ.isError &&
                  assignments.length > 0 && (
                    <ul className="divide-y divide-input rounded-md border border-input">
                      {assignments.map((row) => (
                        <li
                          key={row.employee_id}
                          className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                        >
                          <span className="font-medium text-gray-900">
                            {row.employee_full_name}
                          </span>
                          <span className="text-muted-foreground">
                            {row.employee_email}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
              </CardContent>
            </Card>
          </>
        )}
      </TimesheetFeatureGate>
    </TimeAndLeaveShell>
  );
}
